import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  MenuItem,
  alpha,
  useTheme,
  Chip,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  Description,
  Assessment,
  TrendingUp,
  BusinessCenter,
  Info,
  AddBusiness,
  CheckCircle,
  Close,
  FiberManualRecord,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import type { Notification, NotificationType } from '../../../types/types';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead?: (id: string) => void;
  onDelete?: (id: string) => void;
  onClick?: (notification: Notification) => void;
  compact?: boolean;
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'document_processed':
      return <Description fontSize="small" />;
    case 'analysis_complete':
      return <Assessment fontSize="small" />;
    case 'valuation_complete':
      return <TrendingUp fontSize="small" />;
    case 'deal_update':
      return <BusinessCenter fontSize="small" />;
    case 'hotel_added':
      return <AddBusiness fontSize="small" />;
    case 'report_ready':
      return <CheckCircle fontSize="small" />;
    case 'weekly_summary':
      return <Assessment fontSize="small" />;
    case 'system':
    default:
      return <Info fontSize="small" />;
  }
};

const getNotificationColor = (type: NotificationType, theme: Theme) => {
  switch (type) {
    case 'document_processed':
      return theme.palette.info.main;
    case 'analysis_complete':
      return theme.palette.success.main;
    case 'valuation_complete':
      return theme.palette.success.main;
    case 'deal_update':
      return theme.palette.warning.main;
    case 'hotel_added':
      return theme.palette.primary.main;
    case 'report_ready':
      return theme.palette.success.main;
    case 'weekly_summary':
      return theme.palette.secondary.main;
    case 'system':
    default:
      return theme.palette.text.secondary;
  }
};

const getPriorityColor = (priority: string, theme: Theme) => {
  switch (priority) {
    case 'urgent':
      return theme.palette.error.main;
    case 'high':
      return theme.palette.warning.main;
    case 'medium':
      return theme.palette.info.main;
    case 'low':
    default:
      return theme.palette.text.secondary;
  }
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
  onClick,
  compact = false,
}) => {
  const theme = useTheme();
  const iconColor = getNotificationColor(notification.type, theme);

  const handleClick = () => {
    if (!notification.read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
    if (onClick) {
      onClick(notification);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(notification.id);
    }
  };

  return (
    <MenuItem
      onClick={handleClick}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
        backgroundColor: notification.read
          ? 'transparent'
          : alpha(theme.palette.primary.main, 0.05),
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: alpha(theme.palette.action.hover, 0.1),
        },
        transition: 'all 0.2s ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1.5 }}>
        {/* Icon */}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(iconColor, 0.1),
            color: iconColor,
            flexShrink: 0,
          }}
        >
          {getNotificationIcon(notification.type)}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: notification.read ? 400 : 600,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {notification.title}
            </Typography>
            {!notification.read && (
              <FiberManualRecord
                sx={{
                  fontSize: 10,
                  color: theme.palette.primary.main,
                  flexShrink: 0,
                }}
              />
            )}
          </Box>

          {!compact && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {notification.message}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
            </Typography>

            {notification.priority !== 'medium' && (
              <Chip
                label={notification.priority}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  backgroundColor: alpha(getPriorityColor(notification.priority, theme), 0.1),
                  color: getPriorityColor(notification.priority, theme),
                  '& .MuiChip-label': {
                    px: 1,
                  },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Actions */}
        {onDelete && (
          <IconButton
            size="small"
            onClick={handleDelete}
            sx={{
              flexShrink: 0,
              opacity: 0.5,
              '&:hover': {
                opacity: 1,
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
              },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>
    </MenuItem>
  );
};

export default NotificationItem;
